import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AlertCircle, Loader2 } from 'lucide-react'
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { api } from '../../lib/api'
import { currentLocaleTag } from '../../lib/formatting'
import { formatCents } from '../../lib/money'
import type {
  RecurringMetricsPoint,
  RecurringMetricsResponse,
  RecurringMetricsSlice,
} from '@jr2os/shared'

type Metric = 'mrr' | 'arr' | 'movement'

/**
 * Mini dashboard de «Pagos recurrentes»: MRR y ARR de los planes, por año y mes, con la evolución mes a mes, de qué se compone
 * (por cliente y por periodicidad, en MRR o en ARR según lo elegido en la gráfica) y las altas y bajas de cada mes.
 *
 * Las cifras vienen del servidor (`GET /recurring-invoices/metrics`, ver `lib/recurring-metrics.ts`): se calculan sobre
 * los planes vigentes, no sobre lo facturado. `refreshKey` cambia cuando se edita la tabla de debajo, para recalcular.
 */
export default function RecurringMetrics({ refreshKey }: { refreshKey: unknown }) {
  const { t } = useTranslation('settings')
  const tag = currentLocaleTag()
  const thisYear = new Date().getFullYear()
  const [year, setYear] = useState(thisYear)
  const [month, setMonth] = useState<number | null>(null)
  const [metric, setMetric] = useState<Metric>('mrr')
  const [data, setData] = useState<RecurringMetricsResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(false)
    const qs = new URLSearchParams({ year: String(year) })
    if (month != null) qs.set('month', String(month))
    api
      .get<RecurringMetricsResponse>(`/recurring-invoices/metrics?${qs.toString()}`)
      .then((resp) => {
        if (!cancelled) setData(resp)
      })
      .catch(() => {
        if (!cancelled) {
          setError(true)
          setData(null)
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [year, month, refreshKey])

  const monthName = (ym: string, style: 'short' | 'long' = 'short') =>
    new Date(`${ym}-01T00:00:00Z`).toLocaleDateString(tag, { month: style, timeZone: 'UTC' })
  const monthYear = (ym: string) =>
    new Date(`${ym}-01T00:00:00Z`).toLocaleDateString(tag, {
      month: 'long',
      year: 'numeric',
      timeZone: 'UTC',
    })

  const value = (p: RecurringMetricsPoint, m: 'mrr' | 'arr') =>
    m === 'mrr' ? p.mrrCents : p.arrCents

  /** «+120,00 € (+8,5 %) frente al mes anterior», en verde si sube y en rojo si baja. */
  const delta = (now: number, before: number, label: string) => {
    const diff = now - before
    if (diff === 0) {
      return <span className="t-faint">{t('recurring.metrics.noChange', { label })}</span>
    }
    const pct =
      before > 0
        ? ` (${diff > 0 ? '+' : '−'}${((Math.abs(diff) / before) * 100).toLocaleString(tag, { maximumFractionDigits: 1 })} %)`
        : ''
    return (
      <span
        className={
          diff > 0 ? 'text-emerald-700 dark:text-emerald-300' : 'text-rose-700 dark:text-rose-300'
        }
      >
        {diff > 0 ? '+' : '−'}
        {formatCents(Math.abs(diff))}
        {pct} <span className="t-faint">{label}</span>
      </span>
    )
  }

  /**
   * Barras de reparto (cliente o periodicidad): el nombre, su MRR o su ARR (el que está elegido en la gráfica de debajo), su
   * parte y una barra proporcional. La parte es la misma en MRR y en ARR (ARR = MRR × 12).
   */
  const slices = (
    rows: Array<RecurringMetricsSlice & { shown: string }>,
    testId: string,
    unit: 'mrr' | 'arr'
  ) => (
    <ul className="space-y-2" data-testid={testId}>
      {rows.map((r) => (
        <li key={r.key}>
          <div className="flex items-baseline justify-between gap-2 text-xs">
            <span className="t-primary truncate" title={r.shown}>
              {r.shown}{' '}
              <span className="t-faint">
                · {t('recurring.metrics.plansCount', { count: r.plans })}
              </span>
            </span>
            <span className="font-mono t-secondary whitespace-nowrap">
              {formatCents(unit === 'arr' ? r.arrCents : r.mrrCents)}{' '}
              <span className="t-faint">
                {(r.shareBps / 100).toLocaleString(tag, { maximumFractionDigits: 1 })} %
              </span>
            </span>
          </div>
          <div className="h-1.5 rounded bg-[var(--bg-input-soft)] mt-1 overflow-hidden">
            <div
              className="h-full bg-jr2-500"
              style={{ width: `${Math.min(100, r.shareBps / 100)}%` }}
            />
          </div>
        </li>
      ))}
    </ul>
  )

  const header = (
    <div className="flex items-end justify-between gap-3 flex-wrap">
      <div>
        <h3 className="text-sm font-semibold t-primary">{t('recurring.metrics.title')}</h3>
        <p className="text-xs t-muted mt-0.5 max-w-2xl">{t('recurring.metrics.subtitle')}</p>
      </div>
      <div className="flex items-center gap-2">
        <label className="text-xs t-muted flex items-center gap-1.5">
          {t('recurring.metrics.year')}
          <select
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
            className="bg-transparent border border-base rounded px-2 py-1 text-sm t-secondary focus:outline-none focus:border-jr2-500/50"
            data-testid="metrics-year"
          >
            {(data?.years ?? [year]).map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </label>
        <label className="text-xs t-muted flex items-center gap-1.5">
          {t('recurring.metrics.month')}
          <select
            value={month ?? ''}
            onChange={(e) => setMonth(e.target.value === '' ? null : Number(e.target.value))}
            className="bg-transparent border border-base rounded px-2 py-1 text-sm t-secondary focus:outline-none focus:border-jr2-500/50"
            data-testid="metrics-month"
          >
            <option value="">{t('recurring.metrics.allYear')}</option>
            {Array.from({ length: 12 }, (_, m) => (
              <option key={m} value={m + 1}>
                {monthName(`2000-${String(m + 1).padStart(2, '0')}`, 'long')}
              </option>
            ))}
          </select>
        </label>
      </div>
    </div>
  )

  if (error) {
    return (
      <div className="surface p-4 space-y-3" data-testid="recurring-metrics">
        {header}
        <div className="p-3 text-sm rounded-lg border text-rose-700 dark:text-rose-300 bg-rose-500/10 border-rose-500/30 flex items-center gap-2">
          <AlertCircle size={14} /> {t('recurring.metrics.error')}
        </div>
      </div>
    )
  }
  if (!data) {
    return (
      <div className="surface p-4 space-y-3" data-testid="recurring-metrics">
        {header}
        <div className="p-6 text-center t-muted text-sm">
          <Loader2 size={14} className="inline-block animate-spin mr-2" />
          {t('recurring.metrics.loading')}
        </div>
      </div>
    )
  }

  const ref = data.reference
  const empty = data.months.every((m) => m.plans === 0) && ref.plans === 0
  const refLabel = monthYear(ref.month)
  const arpaCents = ref.clients > 0 ? Math.round(ref.mrrCents / ref.clients) : 0
  // Tolerante: una API anterior a la composición (despliegue a medias) no debe tumbar la pantalla.
  const composition = data.composition ?? { byClient: [], byPeriodicity: [] }
  const topClient = composition.byClient[0]
  // Los clientes pequeños se agrupan en «Otros» para que el reparto quepa en pantalla.
  const MAX_CLIENT_ROWS = 6
  // Con UN solo cliente de más no se esconde tras «Otros»: se muestra él.
  const hideFrom =
    composition.byClient.length - MAX_CLIENT_ROWS > 1
      ? MAX_CLIENT_ROWS
      : composition.byClient.length
  const clientRows = composition.byClient.slice(0, hideFrom).map((c) => ({ ...c, shown: c.label }))
  const restClients = composition.byClient.slice(hideFrom)
  if (restClients.length > 0) {
    // Se suma el ARR exacto y se redondea AL FINAL: sumar partes ya redondeadas acumula el error de cada una.
    const restArr = restClients.reduce((a, c) => a + c.arrCents, 0)
    clientRows.push({
      key: '__others__',
      label: t('recurring.metrics.others'),
      shown: t('recurring.metrics.others'),
      arrCents: restArr,
      mrrCents: Math.round(restArr / 12),
      plans: restClients.reduce((a, c) => a + c.plans, 0),
      shareBps: ref.arrCents > 0 ? Math.round((restArr * 10_000) / ref.arrCents) : 0,
    })
  }
  const periodicityRows = composition.byPeriodicity.map((p) => ({
    ...p,
    shown: t(`enums:RecurringInvoicePeriodicity.${p.key}`, { defaultValue: p.key }),
  }))
  // El reparto de arriba sigue a la gráfica: en ARR se enseña el ARR de cada cliente y periodicidad; en MRR y en «Altas y bajas»
  // (que va en MRR), el MRR.
  const unit: 'mrr' | 'arr' = metric === 'arr' ? 'arr' : 'mrr'
  const movementData = data.months.map((p) => ({ ...p, lostNeg: -p.lostMrrCents }))
  const yearNew = data.months.reduce((a, p) => a + p.newMrrCents, 0)
  const yearLost = data.months.reduce((a, p) => a + p.lostMrrCents, 0)

  return (
    <div className="surface p-4 space-y-3" data-testid="recurring-metrics" aria-busy={loading}>
      {header}

      <p className="text-xs t-faint" data-testid="metrics-reference">
        {t(ref.projected ? 'recurring.metrics.referenceProjected' : 'recurring.metrics.reference', {
          month: refLabel,
        })}
      </p>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-2">
        <div className="rounded-lg border border-base px-3 py-2">
          <div className="text-[10px] uppercase tracking-wider t-muted">
            {t('recurring.metrics.mrr')}
          </div>
          <div className="font-mono text-lg t-primary mt-0.5" data-testid="metrics-mrr">
            {formatCents(ref.mrrCents)}
          </div>
          <div className="text-[11px] mt-0.5">
            {delta(ref.mrrCents, data.previousMonth.mrrCents, t('recurring.metrics.vsPrevMonth'))}
          </div>
        </div>
        <div className="rounded-lg border border-base px-3 py-2">
          <div className="text-[10px] uppercase tracking-wider t-muted">
            {t('recurring.metrics.arr')}
          </div>
          <div className="font-mono text-lg t-primary mt-0.5" data-testid="metrics-arr">
            {formatCents(ref.arrCents)}
          </div>
          <div className="text-[11px] mt-0.5">
            {delta(ref.arrCents, data.previousYear.arrCents, t('recurring.metrics.vsPrevYear'))}
          </div>
        </div>
        <div className="rounded-lg border border-base px-3 py-2">
          <div className="text-[10px] uppercase tracking-wider t-muted">
            {t('recurring.metrics.plans')}
          </div>
          <div className="font-mono text-lg t-primary mt-0.5" data-testid="metrics-plans">
            {ref.plans}
          </div>
          <div className="text-[11px] t-faint mt-0.5">
            {t('recurring.metrics.clients', { count: ref.clients })}
            {ref.clients > 0 && (
              <>
                {' · '}
                {t('recurring.metrics.arpa', { amount: formatCents(arpaCents) })}
              </>
            )}
          </div>
        </div>
        <div className="rounded-lg border border-base px-3 py-2">
          <div className="text-[10px] uppercase tracking-wider t-muted">
            {t('recurring.metrics.concentration')}
          </div>
          {topClient ? (
            <>
              <div
                className="font-mono text-lg t-primary mt-0.5"
                data-testid="metrics-concentration"
              >
                {(topClient.shareBps / 100).toLocaleString(tag, { maximumFractionDigits: 1 })} %
              </div>
              <div className="text-[11px] t-faint mt-0.5 truncate" title={topClient.label}>
                {topClient.label}
              </div>
            </>
          ) : (
            <div className="font-mono text-lg t-faint mt-0.5">—</div>
          )}
        </div>
        <div className="rounded-lg border border-base px-3 py-2">
          <div className="text-[10px] uppercase tracking-wider t-muted">
            {t('recurring.metrics.movement')}
          </div>
          <div className="font-mono text-sm mt-1 text-emerald-700 dark:text-emerald-300">
            {ref.newMrrCents > 0 ? '+' : ''}
            {formatCents(ref.newMrrCents)}{' '}
            <span className="text-[11px] t-faint font-sans">{t('recurring.metrics.new')}</span>
          </div>
          <div className="font-mono text-sm text-rose-700 dark:text-rose-300">
            {ref.lostMrrCents > 0 ? '−' : ''}
            {formatCents(ref.lostMrrCents)}{' '}
            <span className="text-[11px] t-faint font-sans">{t('recurring.metrics.lost')}</span>
          </div>
        </div>
      </div>

      {!empty && ref.plans > 0 && (
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <h4 className="text-xs font-semibold t-secondary mb-2">
              {t(unit === 'arr' ? 'recurring.metrics.byClientArr' : 'recurring.metrics.byClient')}
            </h4>
            {slices(clientRows, 'metrics-by-client', unit)}
          </div>
          <div>
            <h4 className="text-xs font-semibold t-secondary mb-2">
              {t(
                unit === 'arr'
                  ? 'recurring.metrics.byPeriodicityArr'
                  : 'recurring.metrics.byPeriodicity'
              )}
            </h4>
            {slices(periodicityRows, 'metrics-by-periodicity', unit)}
          </div>
        </div>
      )}

      {empty ? (
        <p className="text-sm t-muted text-center py-6">{t('recurring.metrics.empty')}</p>
      ) : (
        <>
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <span className="text-xs font-semibold t-secondary">
              {t('recurring.metrics.chartTitle', { year: data.year })}
            </span>
            <div className="inline-flex rounded-lg border border-base overflow-hidden text-xs">
              {(['mrr', 'arr', 'movement'] as const).map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => setMetric(m)}
                  aria-pressed={metric === m}
                  className={`px-3 py-1 font-semibold ${
                    metric === m ? 'bg-jr2-600 text-white' : 't-muted hover:bg-hover'
                  }`}
                >
                  {m === 'mrr'
                    ? t('recurring.metrics.mrr')
                    : m === 'arr'
                      ? t('recurring.metrics.arr')
                      : t('recurring.metrics.chartMovement')}
                </button>
              ))}
            </div>
          </div>
          {metric === 'movement' ? (
            <>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={movementData} stackOffset="sign">
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                  <XAxis
                    dataKey="month"
                    tickFormatter={(m: string) => monthName(m)}
                    tick={{ fontSize: 11, fill: 'var(--text-muted, #94a3b8)' }}
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: 'var(--text-muted, #94a3b8)' }}
                    tickFormatter={(v: number) => formatCents(v)}
                    width={90}
                  />
                  <Tooltip
                    cursor={{ fill: 'rgba(139,92,246,0.08)' }}
                    labelFormatter={(m: string) => monthYear(m)}
                    formatter={(v: number | string, name: string) => [
                      formatCents(Math.abs(Number(v))),
                      name,
                    ]}
                    contentStyle={{
                      background: 'var(--bg-input)',
                      border: '1px solid var(--border)',
                      borderRadius: 8,
                      fontSize: 12,
                    }}
                  />
                  <Bar
                    dataKey="newMrrCents"
                    name={t('recurring.metrics.chartNew')}
                    stackId="mov"
                    fill="#10b981"
                    radius={[3, 3, 0, 0]}
                  >
                    {movementData.map((p) => (
                      <Cell key={p.month} fill="#10b981" fillOpacity={p.projected ? 0.35 : 0.9} />
                    ))}
                  </Bar>
                  {/* En recharts el radio se aplica desde el EXTREMO del valor: en una barra negativa también [3,3,0,0]. */}
                  <Bar
                    dataKey="lostNeg"
                    name={t('recurring.metrics.chartLost')}
                    stackId="mov"
                    fill="#f43f5e"
                    radius={[3, 3, 0, 0]}
                  >
                    {movementData.map((p) => (
                      <Cell key={p.month} fill="#f43f5e" fillOpacity={p.projected ? 0.35 : 0.9} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
              <p className="text-xs t-secondary" data-testid="metrics-movement-totals">
                {t('recurring.metrics.movementTotals', {
                  year: data.year,
                  added: formatCents(yearNew),
                  lost: formatCents(yearLost),
                  net: `${yearNew - yearLost >= 0 ? '+' : '−'}${formatCents(Math.abs(yearNew - yearLost))}`,
                })}
              </p>
            </>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={data.months}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                <XAxis
                  dataKey="month"
                  tickFormatter={(m: string) => monthName(m)}
                  tick={{ fontSize: 11, fill: 'var(--text-muted, #94a3b8)' }}
                />
                <YAxis
                  tick={{ fontSize: 11, fill: 'var(--text-muted, #94a3b8)' }}
                  tickFormatter={(v: number) => formatCents(v)}
                  width={90}
                />
                <Tooltip
                  cursor={{ fill: 'rgba(139,92,246,0.08)' }}
                  labelFormatter={(m: string) => monthYear(m)}
                  formatter={(
                    _v: number | string,
                    _n: string,
                    item: { payload?: RecurringMetricsPoint }
                  ) => {
                    const p = item.payload
                    return [
                      p ? formatCents(value(p, metric === 'arr' ? 'arr' : 'mrr')) : '',
                      metric === 'mrr' ? t('recurring.metrics.mrr') : t('recurring.metrics.arr'),
                    ]
                  }}
                  contentStyle={{
                    background: 'var(--bg-input)',
                    border: '1px solid var(--border)',
                    borderRadius: 8,
                    fontSize: 12,
                  }}
                />
                <Bar dataKey={metric === 'arr' ? 'arrCents' : 'mrrCents'} radius={[3, 3, 0, 0]}>
                  {data.months.map((p) => (
                    <Cell
                      key={p.month}
                      fill="#8b5cf6"
                      fillOpacity={p.projected ? 0.35 : p.month === ref.month ? 1 : 0.75}
                      stroke={p.month === ref.month ? '#6d28d9' : undefined}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
          <p className="text-[11px] t-faint">{t('recurring.metrics.footnote')}</p>
        </>
      )}
    </div>
  )
}
